import { createFileRoute } from '@tanstack/react-router'
import StaffListPage from '../../Pages/StaffListPage/StaffListPage'
import StaffListPageHeader from '../../Pages/StaffListPage/StaffListPageHeader'
import PageBody from '../../Pages/Layout/PageBody'
import SessionStore from '../../State/SessionStore'
import { qf } from '../../Queries/queryFormatter'
import LoadingSpinner from '../../Components/LoadingSpinner'
import ErrorComponent from '../../Components/ErrorComponent'
import {
    canViewRoute,
    migratedToV2,
} from '../../State/Permissions/HasPermissions'
import PermissionDenied from '../../Pages/PermissionDenied'
import { setLayoutDefaults } from '@2/layout/layout-store'
import { getCacheItemById } from '@2/cache'
import { StaffReportPage } from '@2/pages/staff-report/staff-report-page'
import { trpc } from '../../system/trpc'
import { StaffReportColumns } from '../../reports/Staff/StaffReportColumns'
import {
    setOrganisationReport,
    setReportData,
} from '@2/pages/staff-report/staff-report-store'
import { dateStringLookup } from '../../Components/Filters'
import { queryClient } from '../../App'
import { PageBody as V2PageBody } from '@2/layout/page-body'
import { processTRPCData } from '../../Queries/fetchData'

export const Route = createFileRoute('/_private/staff/')({
    beforeLoad: ({ search }) => {
        if (!canViewRoute(SessionStore.user, 'staffList', search))
            throw new Error('Permission Denied')
        const report = getCacheItemById('organisationReports', search.report)
        setLayoutDefaults({
            pageTitle: report?.name || 'Staff Report',
            subMenu: 'resources',
        })
    },
    errorComponent: ({ error, reset }) => {
        if (error.message === 'Permission Denied') return <PermissionDenied />
        return <ErrorComponent error={error} reset={reset} />
    },
    pendingComponent: LoadingSpinner,
    loader: async ({ location }) => await loader(location.search),
    loaderDeps: ({ search: { report } }) => ({
        report,
    }),
    component: Wrapper,
})

const loader = async ({ report }) => {
    const reportId = report || SessionStore.organisation?.defaultStaffReportId
    if (migratedToV2()) {
        const res = reportId
            ? await trpc.staffReport.getOrganisationReport.query({
                  reportId,
              })
            : null

        const organisationReport =
            res || SessionStore.organisation.defaultStaffReport
        let dateRange = [null, null]
        if (organisationReport.options?.dateRange) {
            dateRange =
                dateStringLookup[organisationReport.options?.dateRange]?.(
                    organisationReport.options?.fortnightType
                ) || organisationReport.options?.dateRange
        }
        const { columns, filters, groupBy, sortBy, options, name } =
            organisationReport
        setOrganisationReport(
            {
                columns,
                filters,
                groupBy,
                organisationId: SessionStore.organisationId,
                sortBy,
                dateRange,
                invoiceDateType: SessionStore.settings.reportInvoiceDateType,
                id: reportId,
                options,
                name,
            },
            { needsRefetch: false }
        )
        const cacheData = await queryClient.fetchQuery({
            queryKey: ['staffReportCache', reportId],
            queryFn: () =>
                trpc.staffReport.getReportsData.query({
                    organisationId: SessionStore.organisationId,
                    userId: SessionStore.user?.id,
                    columns: organisationReport?.columns.filter(
                        (c) =>
                            !StaffReportColumns(reportId)[c]?.permissions ||
                            StaffReportColumns(reportId)[c].permissions()
                    ),
                    filters: organisationReport?.filters.map((f) => {
                        if (
                            StaffReportColumns(reportId)[f.column]?.type ===
                            'date'
                        ) {
                            let value = dateStringLookup[f.value]?.() || f.value
                            if (Array.isArray(value)) {
                                value = value.map((d) =>
                                    d ? format(d, 'yyyy-MM-dd') : null
                                )
                            } else {
                                value = value
                                    ? format(value, 'yyyy-MM-dd')
                                    : null
                            }
                            return {
                                ...f,
                                value,
                            }
                        } else if (
                            StaffReportColumns(reportId)[f.column]?.type ===
                            'staff'
                        ) {
                            let value = f.value
                            if (Array.isArray(value)) {
                                value = value.map((d) => {
                                    d = new Date(d)
                                    return d && d.getTime()
                                        ? format(d, 'yyyy-MM-dd HH:mm')
                                        : null
                                })
                            } else {
                                value = new Date(value)
                                value =
                                    value && value.getTime()
                                        ? format(value, 'yyyy-MM-dd HH:mm')
                                        : null
                            }
                            return {
                                ...f,
                                value,
                            }
                        } else {
                            return f
                        }
                    }),
                    sortBy: organisationReport?.sortBy || [],
                    groupBy: organisationReport?.groupBy || [],
                    dateRange,
                    invoiceDateType:
                        SessionStore.settings.reportInvoiceDateType,
                }),
            staleTime: 5 * 60 * 1000,
        })

        setReportData(cacheData)
    }
    const data = await trpc.legacy.getStaffListPageData.query({ reportId })
    return processTRPCData(data)
}

function Wrapper() {
    const { report } = Route.useSearch()
    if (migratedToV2()) {
        return (
            <V2PageBody>
                <StaffReportPage />
            </V2PageBody>
        )
    }
    return (
        <>
            <StaffListPageHeader id={report} />
            <PageBody>
                <StaffListPage id={report} />
            </PageBody>
        </>
    )
}
